import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useLayoutEffect,
} from 'react';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Table,
  Spinner,
} from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import useTableHook from '../../hooks/useTableHook';
import {
  SelectInput,
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  UserAsyncAutocompleteSelect,
  TablePagination,
} from '../../Common';
import {
  fetchRequestTypes,
  fetchMyApprovals,
  fetchTableColumnValues,
  approveRejectRequest,
  approveRejectRequestList,
} from '../../../store/apis/approvalService';
import { fetchDelegateApprovalAutoComplete } from '../../../store/apis/autocompleteService';
import {
  myApprovalsHeaders,
  approvalsActions,
  renderRequestLegend,
} from '../constants';
import RejectRequestModal from '../RejectRequestModal';
import * as icons from '../../../assets';
import { useConfirmationModal } from '../../hooks';
import { Messages } from '../../../common/messages';

import './MyApprovals.scss';

const MyApprovals = forwardRef(
  ({ setCounts, setSelectedRows, handleEditClick }, ref) => {
    const queryClient = useQueryClient();
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectionType, setRejectionType] = useState('single');
    const [rejectionRequest, setRejectionRequest] = useState(null);
    const { open, ConfirmationModal } = useConfirmationModal();

    const toggleRejectModal = () => {
      setRejectModal(!rejectModal);
    };

    // Table Hook
    const {
      data,
      columnData,
      pageRequest,
      setPageRequest,
      externalFilters,
      applyFilter,
      columnName,
      setColumnName,
      activeRows,
      handleRowCheck,
      selectAllRows,
      resetFilters,
      handleSetExternalFilters,
      applyExternalFilters,
      resetActiveRows,
      handleManualSort,
      clearSingleFilterValue,
      checkColumnIfSorted,
      isLoadingData,
      isLoadingColumn,
      nextPage,
      prevPage,
      getPageSize,
      setPageSize,
      handleAutoSort,
    } = useTableHook({
      defaultPageRequest: {
        pageNumber: 1,
        pageSize: 50,
        sortColumn: 'RequestDate',
        sortDirection: 'DESC',
        filters: [],
      },
      fetchFn: fetchMyApprovals,
      fetchColumnFn: fetchTableColumnValues,
      queryKeyPrefix: 'MyApprovals',
      columnKeyPrefix: 'MyApprovalsColumn',
      tableName: 'MyApprovals',
      defaultExternalFilters: {
        category: '',
        requestorId: 0,
        secApprovalStatus: 'N',
      },
      tableQueryOptions: {
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        keepPreviousData: true,
      },
    });

    // Set Counts
    useEffect(() => {
      setCounts(prev => ({
        ...prev,
        myApprovals: data?.totalCount || 0,
      }));
    }, [data?.totalCount, setCounts]);

    useEffect(() => {
      setSelectedRows(activeRows.length > 0);
    }, [setSelectedRows, activeRows]);

    const { data: requestTypes } = useQuery({
      queryKey: ['requestTypes'],
      queryFn: fetchRequestTypes,
      keepPreviousData: true,
    });

    const approveRejectRequestMutation = useMutation({
      mutationFn: approveRejectRequest,
      onSuccess: (_data, variables) => {
        if (variables?.actionType === 'R') {
          setRejectModal(false);
          setRejectReason('');
          setRejectionRequest(null);
          toast.success(`${variables?.requestType} rejected successfully`, {
            className: 'toast-success',
          });
        } else if (variables?.actionType === 'A') {
          toast.success(`${variables?.requestType} approved successfully`, {
            className: 'toast-success',
          });
        }
        queryClient.invalidateQueries(['MyApprovals']);
      },
      onError: error => {
        const errorMessage =
          error?.err?.meta?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'An error occurred';
        toast.error(errorMessage, {
          className: 'toast-error',
        });
      },
    });

    const approveRejectRequestListMutation = useMutation({
      mutationFn: approveRejectRequestList,
      onSuccess: (_data, variables) => {
        if (variables[0]?.actionType === 'R') {
          setRejectModal(false);
          setRejectReason('');
          setRejectionRequest(null);
          toast.success('Approvals rejected successfully', {
            className: 'toast-success',
          });
        } else if (variables[0]?.actionType === 'A') {
          toast.success('Approvals approved successfully', {
            className: 'toast-success',
          });
        }
        queryClient.invalidateQueries(['MyApprovals']);
        resetActiveRows();
      },
      onError: error => {
        const errorMessage =
          error?.err?.meta?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'An error occurred';
        toast.error(errorMessage, {
          className: 'toast-error',
        });
      },
    });

    // Mapping function for request payload
    const mapApproveRejectRequest = (action, approval) => ({
      actionType: action,
      requestId: approval.RequestID,
      requestedBy: approval.RequestedBy,
      category: approval.Category,
      typ: approval?.Typ,
      approvalRank: approval?.ApprovalRank,
      requestedDate: approval.RequestDate,
      requestType: approval.RequestType,
      details: approval.Details,
      requestorId: approval.Requester,
      coCd: approval?.CoCd,
      reason: approval?.Comment,
    });

    // Handle approve and rejection of approval
    const handleApproveRejectApproval = async (action, approval) => {
      const requestPayload = mapApproveRejectRequest(action, approval);
      if (action === approvalsActions.Reject) {
        if (!rejectReason) {
          toast.error('Please provide a reason for rejection', {
            className: 'toast-error',
          });
          return;
        }
        requestPayload.reason = rejectReason;
      }
      approveRejectRequestMutation.mutate(requestPayload);
    };

    // Handle approve and rejection of multiple approvals
    const handleApproveRejectApprovalList = async (
      action,
      approvals,
      rowList,
    ) => {
      if (rowList.length === 0) {
        toast.error('Please select at least one row', {
          className: 'toast-error',
        });
        return;
      }

      if (action === approvalsActions.Reject && !rejectReason) {
        toast.error('Please provide a reason for rejection', {
          className: 'toast-error',
        });
        return;
      }

      const requestPayloadList = rowList.map(index => {
        const approval = approvals[index];
        const payload = mapApproveRejectRequest(action, approval);
        if (action === approvalsActions.Reject) {
          payload.reason = rejectReason;
        }
        return payload;
      });
      approveRejectRequestListMutation.mutate(requestPayloadList);
    };

    const renderTableBody = () => {
      const tableData = data?.data;

      if (!tableData || tableData.length === 0) {
        return (
          <tr>
            <td className="text-center" colSpan={6}>
              {isLoadingData ? (
                <>
                  <Spinner color="dark" size="sm" />
                  <span className="ms-2">Loading...</span>
                </>
              ) : (
                Messages.NO_APPROVALS
              )}
            </td>
          </tr>
        );
      }

      return tableData.map((item, index) => (
        <tr
          key={index}
          className="cursor-pointer"
          onClick={() => {
            handleEditClick(item, 'approval');
          }}
        >
          <td>
            <div className="form-check px-0">
              <Input
                className="form-check-input blue-checkbox"
                type="checkbox"
                name="chk_child"
                checked={activeRows?.includes(index)}
                onChange={e => {
                  handleRowCheck(index, e.target.checked);
                }}
                onClick={e => e.stopPropagation()}
              />
            </div>
          </td>
          <td>{item.RequestID}</td>
          <td>{item.RequestedBy}</td>
          <td>{item.RequestDate}</td>
          <td>{renderRequestLegend(item.RequestType)}</td>
          <td title={item.Details}>{item.Details}</td>
          <td className="text-center">
            <div className="d-flex flex-row align-items-center justify-content-center gap-2">
              <button
                type="button"
                className="btn-link p-0 border-0 bg-transparent"
                title="Approve the selected request after reviewing the details."
                onClick={e => {
                  e.stopPropagation();
                  open({
                    title: 'Confirm Approval',
                    message: 'Are you sure you want to approve?',
                    buttonType: 'success',
                    onConfirm: () =>
                      handleApproveRejectApproval(
                        approvalsActions.Approve,
                        item,
                      ),
                    showCancelButton: true,
                  });
                }}
              >
                <img src={icons.iconsApproveRequestIcon} alt="" height={14} />
              </button>
              <button
                type="button"
                title="Reject the selected request. A reason is required."
                className="btn-link p-0 border-0 bg-transparent"
                onClick={e => {
                  e.stopPropagation();
                  setRejectionType('single');
                  setRejectionRequest(item);
                  toggleRejectModal();
                }}
              >
                <img src={icons.iconsRejectRequestIcon} alt="" height={14} />
              </button>
              <button
                type="button"
                className="btn-link p-0 border-0 bg-transparent"
                title="View request details before taking action."
              >
                <img src={icons.iconsPreviewRequestIcon} height={17} alt="" />
              </button>
            </div>
          </td>
        </tr>
      ));
    };

    useImperativeHandle(ref, () => ({
      approveAll: () => {
        open({
          title: 'Confirm Approval',
          message: 'Are you sure you want to approve?',
          buttonType: 'success',
          onConfirm: () =>
            handleApproveRejectApprovalList(
              approvalsActions.Approve,
              data?.data,
              activeRows,
            ),
          showCancelButton: true,
        });
      },
      rejectAll: () => {
        setRejectionType('multiple');
        toggleRejectModal();
      },
    }));

    const generateRequestTypes = () => {
      if (!requestTypes || requestTypes.length === 0) {
        return [];
      }
      const requestTypeItems = requestTypes?.map(item => ({
        Code: item.Code,
        Name: item.Code,
      }));
      requestTypeItems.unshift({ Code: '', Name: 'All' });
      return requestTypeItems;
    };

    useLayoutEffect(() => {
      setSelectedRows(activeRows.length > 0);
    }, [activeRows.length, setSelectedRows]);

    return (
      <div className="my-approvals">
        <div className="external-filter d-flex justify-content-start gap-3 mx-2 mt-2 p-3">
          <Col lg={3} sm={3} className="external-filter-col">
            <b>Request Type</b>
            <SelectInput
              data={generateRequestTypes() || []}
              keyName="Code"
              labelKey="Name"
              valueKey="Code"
              onChange={e =>
                handleSetExternalFilters('category', e.target.value)
              }
              placeholder="Select Request Type"
              hasDefault
              value={externalFilters?.category}
            />
          </Col>
          <Col lg={4} className="external-filter-col">
            <b>Requestor</b>
            <UserAsyncAutocompleteSelect
              autoCompleteFn={input =>
                fetchDelegateApprovalAutoComplete({
                  prefixText: input,
                })
              }
              mappedFn={rawData =>
                rawData.map(item => ({
                  value: item.ID,
                  label: item.Name,
                  name: item.Name,
                  code: item.Cd,
                  designation: item.Designation,
                  image: item?.Imagefile,
                  coCd: item?.CoCd?.trim(),
                }))
              }
              onChange={e => {
                handleSetExternalFilters('requestorId', e);
              }}
              placeholder="Search by Employee Name & ID"
              isClearable
              isSearchable
              value={externalFilters?.requestorId}
              inputIcon={
                <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
              }
              rightKeyAssessor="code"
            />
          </Col>
          <Col lg={3} className="external-filter-col pb-2">
            <div className="d-flex flex-row align-items-center gap-3">
              <FormGroup check className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  id="secondaryApprover"
                  className="form-check-input blue-checkbox"
                  checked={externalFilters?.secApprovalStatus === 'Y'}
                  onChange={e =>
                    handleSetExternalFilters(
                      'secApprovalStatus',
                      e.target.checked ? 'Y' : 'N',
                    )
                  }
                />
                <Label
                  htmlFor="secondaryApprover"
                  className="form-check-label mb-0 align-self-center cursor-pointer"
                  style={{
                    marginTop: '2px',
                  }}
                >
                  Secondary Approval
                </Label>
              </FormGroup>
            </div>
          </Col>
          <Col className="d-flex align-items-center justify-content-end">
            <Button
              className="custom-btn"
              onClick={() =>
                applyExternalFilters({
                  ...externalFilters,
                  requestorId: externalFilters?.requestorId?.value || null,
                })
              }
            >
              Search
            </Button>
          </Col>
        </div>
        <div className="my-2 px-2">
          <TableFilterBubbles
            allFilters={pageRequest?.filters || []}
            clearSingleFilterValue={clearSingleFilterValue}
            labelMapping={myApprovalsHeaders}
            clearAllFilters={resetFilters}
          />
        </div>
        <div className="table-responsive my-approvals-table-container">
          <Table hover className="custom-table text-nowrap my-approval-table">
            <thead>
              <tr>
                <th>
                  <div className="form-check px-0">
                    <Input
                      className="form-check-input blue-checkbox"
                      type="checkbox"
                      id="checkbox"
                      checked={
                        activeRows?.length > 0 &&
                        activeRows?.length === data?.data?.length
                      }
                      onChange={e => selectAllRows(e?.target?.checked)}
                    />
                  </div>
                </th>
                {Object.entries(myApprovalsHeaders).map(([key, label]) => (
                  <th key={key}>
                    <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                      <div
                        className="p-0 cursor-pointer"
                        onClick={() => handleAutoSort(key)}
                      >
                        {label}
                      </div>
                      <TableSortDisplayArrow
                        direction={checkColumnIfSorted(key)}
                      />
                      <FilterDropdown
                        handleSetColumnName={() => {
                          setColumnName(key);
                        }}
                        setColumnName={setColumnName}
                        columnData={columnData}
                        applyFilter={applyFilter}
                        columnName={columnName}
                        pageRequest={pageRequest}
                        setPageRequest={setPageRequest}
                        handleManualSort={handleManualSort}
                        isLoading={isLoadingColumn}
                      />
                    </div>
                  </th>
                ))}
                <th className="text-center" style={{ width: '90px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </div>
        <RejectRequestModal
          isOpen={rejectModal}
          toggle={toggleRejectModal}
          onChange={setRejectReason}
          value={rejectReason}
          handleRejection={() => {
            if (rejectionType === 'single') {
              handleApproveRejectApproval(
                approvalsActions.Reject,
                rejectionRequest,
              );
            }
            if (rejectionType === 'multiple') {
              handleApproveRejectApprovalList(
                approvalsActions.Reject,
                data?.data,
                activeRows,
              );
            }
          }}
        />
        <ConfirmationModal />
        <div className="py-2 px-3 border-top mt-auto">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </div>
      </div>
    );
  },
);

export default MyApprovals;
